import React, { useEffect, useRef } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";

const toOpts = (arr) => (arr || []).map((v) => ({ value: String(v), label: String(v) }));

/* ── FORMULA AUTO-CALC ─────────────────────────────────────────
   A row with Formula = Yes gets its Standard Value calculated from the
   Standard Values typed on the variable rows (e.g. I=4, I1=4, formula
   I+I1 -> 8). No eval(): a tiny parser that only understands numbers,
   variables, + - * / and brackets. Blank if any variable is empty /
   not a number, or on divide-by-zero. */
// FORMULA_CALC_START
const toNum = (s) => {
  const t = String(s ?? "").trim();
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(t) ? Number(t) : null;
};

const evalFormula = (formula, values) => {
  const src = String(formula || "");
  const tokens = src.match(/\d+\.?\d*|\.\d+|[A-Za-z_][A-Za-z0-9_]*|[-+*/()]/g);
  if (!tokens || tokens.join("").length !== src.replace(/\s+/g, "").length) return null;
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expr = () => {
    let v = term();
    while (peek() === "+" || peek() === "-") { const op = next(); const r = term(); v = op === "+" ? v + r : v - r; }
    return v;
  };
  const term = () => {
    let v = factor();
    while (peek() === "*" || peek() === "/") { const op = next(); const r = factor(); v = op === "*" ? v * r : v / r; }
    return v;
  };
  const factor = () => {
    const t = next();
    if (t === undefined) throw new Error("incomplete");
    if (t === "-") return -factor();
    if (t === "+") return factor();
    if (t === "(") { const v = expr(); if (next() !== ")") throw new Error("bracket"); return v; }
    if (/^[A-Za-z_]/.test(t)) {
      const v = values[t.toLowerCase()];
      if (v === undefined) throw new Error("missing variable");
      return v;
    }
    return parseFloat(t);
  };
  try {
    const v = expr();
    return pos === tokens.length && Number.isFinite(v) ? v : null;
  } catch (e) { return null; }
};

const computeFormulaValues = (chars, stdValues) => {
  const vars = {};
  chars.forEach(c => {
    if (c.isFormula || !c.variable) return;
    const n = toNum(stdValues[c.characteristic]);
    if (n !== null) vars[String(c.variable).trim().toLowerCase()] = n;
  });
  const out = {};
  chars.forEach(c => {
    if (!c.isFormula) return;
    const r = evalFormula(c.formula, vars);
    out[c.characteristic] = r === null ? "" : String(parseFloat(r.toFixed(6)));
  });
  return out;
};

// Keeps the formula rows' std values in sync while the user types.
const useAutoFormulaValues = (chars, values, setValues) => {
  useEffect(() => {
    if (!chars || !chars.some(c => c.isFormula)) return;
    const computed = computeFormulaValues(chars, values || {});
    const changed = Object.keys(computed).some(k => ((values || {})[k] ?? "") !== computed[k]);
    if (changed) setValues(prev => ({ ...prev, ...computed }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chars, values]);
};
// FORMULA_CALC_END

/* ══════════════════════════════════════════════════════════════
   ASSIGNMENT TAB
   Filter bar + assignment list + the Assign / Edit-Assignment modals.

   IMPORTANT: this no longer receives `allProducts` (the old ~24k-row
   dump). Cascading filter options come from `assignFilterOptions`,
   fetched by the parent from the scoped GET /assignment-filter-options
   endpoint whenever assignProductType/prodFilterSelections change.
   Product Code <-> Description matching uses lookupProductByCode /
   lookupProductByDescription (parent-provided, hit GET /product-lookup)
   instead of searching a client-held copy of the whole table.
══════════════════════════════════════════════════════════════ */
function InspectionAssignmentTab({
  styles, filterSelectStyles, customSelectStyles, DeleteIcon, ddl,
  assignmentList, handleOpenEditAssign, handleDeleteAssignment,

  assignProductType, setAssignProductType,
  prodFilterSelections, setProdFilterSelections,
  assignProductCode, setAssignProductCode,
  assignProductDesc, setAssignProductDesc,
  prodMatchCount,
  assignFilterOptions,          // { productCategory, subCategory, qualityCategory, cableType, code, colour, noOfCores, nominalCrossSection, referenceOfStandard, voltage, construction }
  lookupProductByCode, lookupProductByDescription, searchProducts,

  handleOpenAssignModal,
  showAssignModal, setShowAssignModal,
  assignGtpSelected, setAssignGtpSelected,
  templateOptions,
  assignGtpCharacteristics, setAssignGtpCharacteristics,
  assignStdValues, setAssignStdValues, setAssignInspTestType,
  assignMatchCount, assignSaving, handleAssignSave,

  showEditAssignModal, setShowEditAssignModal,
  editAssignRow, setEditAssignRow,
  editAssignChars, setEditAssignChars,
  editAssignStdValues, setEditAssignStdValues,
  editAssignMode, setEditAssignMode,
  editAssignSaving, handleEditAssignSave,
}) {
  // Debounced product search for the Product Code / Description dropdowns
  const searchTimer = useRef(null);
  const loadProductOptions = (input) => new Promise((resolve) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      resolve(await searchProducts(input));
    }, 300);
  });
  // Formula rows: Standard Value is calculated automatically (create + edit modals)
  useAutoFormulaValues(assignGtpCharacteristics, assignStdValues, setAssignStdValues);
  useAutoFormulaValues(editAssignChars, editAssignStdValues, setEditAssignStdValues);

  const prodActiveFilterCount = Object.values(prodFilterSelections).reduce((s, a) => s + (a?.length || 0), 0)
    + (assignProductType ? 1 : 0) + (assignProductCode ? 1 : 0);

  const opts = assignFilterOptions || {};
  const categoryOptions        = opts.productCategory || [];
  const cableTypeOptions       = opts.cableType || [];
  const codeOptions            = opts.code || [];
  const colourOptions          = opts.colour || [];
  const coreOptions            = opts.noOfCores || [];
  const crossSectionOptions    = opts.nominalCrossSection || [];
  const standardOptions        = opts.referenceOfStandard || [];
  const voltageOptions         = opts.voltage || [];
  const constructionOptions    = opts.construction || [];
  const rawCategoryOptions     = opts.productCategory || [];
  const rawSubCategoryOptions  = opts.subCategory || [];
  const rawQualityCategoryOptions = opts.qualityCategory || [];

  const renderAssignFilterBar = () => {
    const isFinished = assignProductType.toLowerCase().includes("finished");
    const isRaw      = assignProductType.toLowerCase().includes("raw");

    const makeSelect = (key, label, opts, extraOnChange) => (
      <div key={key} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 190, display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={styles.filterLabel}>{label}</span>
        <Select
          isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
          options={opts}
          value={prodFilterSelections[key] || []}
          onChange={(selectedOpts) => {
            setProdFilterSelections(prev => {
              const next = { ...prev, [key]: selectedOpts || [] };
              if (key === "productCategory") { next.cableType = []; next.code = []; next.subCategory = []; next.qualityCategory = []; }
              if (key === "cableType") { next.code = []; }
              return next;
            });
            if (extraOnChange) extraOnChange(selectedOpts);
          }}
          placeholder={label} isClearable
          menuPortalTarget={document.body} menuPosition="fixed"
          styles={filterSelectStyles}
        />
      </div>
    );

    const clearAll = () => {
      setAssignProductType("");
      setProdFilterSelections({ productCategory: [], subCategory: [], qualityCategory: [], cableType: [], code: [], colour: [], noOfCores: [], nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [], productCode: [], testType: [] });
      setAssignProductCode("");
      setAssignProductDesc("");
    };

    return (
      <div style={styles.filterCard}>
        {/* ROW 1 — Product Type */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 12 }}>
          <div style={{ minWidth: 200, display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={styles.filterLabel}>Product Type</span>
            <select className="form-select form-select-sm" style={{ fontSize: 12, height: 30 }}
              value={assignProductType}
              onChange={e => {
                setAssignProductType(e.target.value);
                setProdFilterSelections({ productCategory: [], subCategory: [], qualityCategory: [], cableType: [], code: [], colour: [], noOfCores: [], nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [], productCode: [], testType: [] });
                setAssignProductCode("");
                setAssignProductDesc("");
              }}>
              <option value="">Select Product Type</option>
              {(ddl.productTypes || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {prodActiveFilterCount > 0 && (
            <button onClick={clearAll} style={styles.clearBtn}>✕ Clear All ({prodActiveFilterCount})</button>
          )}
        </div>

        {/* ROW 2 — Finished Goods filters */}
        {isFinished && (
          <div style={{ ...styles.filterBar, marginBottom: 12 }}>
            {makeSelect("productCategory", "Category",      toOpts(categoryOptions))}
            {makeSelect("cableType",       "Cable Type",    toOpts(cableTypeOptions))}
            {makeSelect("code",            "Code",          toOpts(codeOptions))}
            {makeSelect("colour",          "Color",         toOpts(colourOptions))}
            {makeSelect("noOfCores",       "Cores",         toOpts(coreOptions))}
            {makeSelect("nominalCrossSection", "Cross Section", toOpts(crossSectionOptions))}
            {makeSelect("referenceOfStandard", "Std. Ref",  toOpts(standardOptions))}
            {makeSelect("voltage",         "Voltage",       toOpts(voltageOptions))}
            {makeSelect("construction",    "Construction",  toOpts(constructionOptions))}
          </div>
        )}

        {/* ROW 2 — Raw Material filters */}
        {isRaw && (
          <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            {makeSelect("productCategory", "Category",        toOpts(rawCategoryOptions))}
            {makeSelect("subCategory",     "Sub Category",    toOpts(rawSubCategoryOptions))}
            {makeSelect("qualityCategory", "Quality Category",toOpts(rawQualityCategoryOptions))}
          </div>
        )}

        {/* ROW 3 — Product Code ↔ Description (searchable dropdowns) */}
        {(isFinished || isRaw) && (
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, width: 240 }}>
              <span style={styles.filterLabel}>Product Code</span>
              <AsyncSelect
                key={`code-${assignProductType}-${JSON.stringify(prodFilterSelections)}`}
                defaultOptions
                loadOptions={loadProductOptions}
                value={assignProductCode ? { value: assignProductCode, label: assignProductCode } : null}
                getOptionLabel={(o) => o.label ?? o.productCode}
                getOptionValue={(o) => o.value ?? o.productCode}
                formatOptionLabel={(o) => o.productCode
                  ? (<div><div>{o.productCode}</div><div style={{ fontSize: 11, color: "#6b7280" }}>{o.productDescription}</div></div>)
                  : o.label}
                onChange={(o) => {
                  setAssignProductCode(o ? o.productCode : "");
                  setAssignProductDesc(o ? (o.productDescription || "") : "");
                }}
                placeholder="Select Product Code" isClearable
                noOptionsMessage={() => "No products found"}
                menuPortalTarget={document.body} menuPosition="fixed"
                styles={filterSelectStyles}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 240 }}>
              <span style={styles.filterLabel}>Product Description</span>
              <AsyncSelect
                key={`desc-${assignProductType}-${JSON.stringify(prodFilterSelections)}`}
                defaultOptions
                loadOptions={loadProductOptions}
                value={assignProductDesc ? { value: assignProductDesc, label: assignProductDesc } : null}
                getOptionLabel={(o) => o.label ?? o.productDescription}
                getOptionValue={(o) => o.value ?? o.productCode}
                formatOptionLabel={(o) => o.productCode
                  ? (<div><div>{o.productDescription}</div><div style={{ fontSize: 11, color: "#6b7280" }}>{o.productCode}</div></div>)
                  : o.label}
                onChange={(o) => {
                  setAssignProductDesc(o ? (o.productDescription || "") : "");
                  setAssignProductCode(o ? o.productCode : "");
                }}
                placeholder="Select Product Description" isClearable
                noOptionsMessage={() => "No products found"}
                menuPortalTarget={document.body} menuPosition="fixed"
                styles={filterSelectStyles}
              />
            </div>
          </div>
        )}

        {prodMatchCount !== null && (
          <div style={styles.matchHint}>
            <span style={{ color: prodMatchCount > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
              {prodMatchCount > 0 ? `${prodMatchCount} product${prodMatchCount !== 1 ? "s" : ""} match` : "No products match the current filters"}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
          <button onClick={handleOpenAssignModal} style={{ background: "#021843", color: "#fff", border: "none", borderRadius: "4px", padding: "5px 16px", fontSize: "13px", fontWeight: 500, height: "32px", cursor: "pointer" }}>
            Assign
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderAssignFilterBar()}
      <table className="table" style={styles.erpTable}>
        <thead>
          <tr>
            <th style={styles.erpTh}>Template Number</th>
            <th style={styles.erpTh}>Inspection Test Type</th>
            <th style={{ ...styles.erpTh, textAlign: "right" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {assignmentList.length > 0 ? assignmentList.map(row => (
            <tr key={row.assignment_id}>
              <td style={styles.erpTd}>{row.inspection_no}</td>
              <td style={styles.erpTd}>{row.inspection_type || "—"}</td>
              <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>
                <button className="btn btn-outline-dark btn-sm me-2" onClick={() => handleOpenEditAssign(row, "view")}>View</button>
                <button style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => handleDeleteAssignment(row.assignment_id)} title="Delete"><DeleteIcon /></button>
              </td>
            </tr>
          )) : (
            <tr><td colSpan="8" className="text-center text-muted p-3">No assignments found</td></tr>
          )}
        </tbody>
      </table>

      {/* ═══════════════════════════════════════════════
          MODAL — ASSIGN INSPECTION TO PRODUCTS
      ═══════════════════════════════════════════════ */}
      {showAssignModal && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: 860, height: "auto", maxHeight: "90vh" }}>
            <div style={styles.modalHeader}>
              <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>Assign Inspection</h5>
              <button style={styles.closeBtn} onClick={() => { setShowAssignModal(false); setAssignGtpSelected(null); setAssignGtpCharacteristics([]); setAssignStdValues({}); setAssignInspTestType(""); }}>✖</button>
            </div>
            <div style={{ ...styles.modalBody, padding: "24px 28px", overflowY: "auto" }}>
              {/* Applied filters summary */}
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {assignProductType && (
                    <div style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                      <span style={{ color: "#64748b", fontWeight: 600 }}>Product Type: </span>
                      <span style={{ color: "#1e293b" }}>{assignProductType}</span>
                    </div>
                  )}
                  {[
                    ["Category",       prodFilterSelections.productCategory],
                    ["Sub Category",   prodFilterSelections.subCategory],
                    ["Quality Cat.",   prodFilterSelections.qualityCategory],
                    ["Cable Type",     prodFilterSelections.cableType],
                    ["Code",           prodFilterSelections.code],
                    ["Color",          prodFilterSelections.colour],
                    ["Cores",          prodFilterSelections.noOfCores],
                    ["Cross Section",  prodFilterSelections.nominalCrossSection],
                    ["Std. Ref",       prodFilterSelections.referenceOfStandard],
                    ["Voltage",        prodFilterSelections.voltage],
                    ["Construction",   prodFilterSelections.construction],
                  ].map(([label, arr]) => {
                    const val = arr?.map(o => o.value).join(", ");
                    return val ? (
                      <div key={label} style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                        <span style={{ color: "#64748b", fontWeight: 600 }}>{label}: </span>
                        <span style={{ color: "#1e293b" }}>{val}</span>
                      </div>
                    ) : null;
                  })}
                  {assignProductCode && (
                    <div style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                      <span style={{ color: "#64748b", fontWeight: 600 }}>Product Code: </span>
                      <span style={{ color: "#1e293b" }}>{assignProductCode}</span>
                    </div>
                  )}
                </div>
                {assignMatchCount !== null && (
                  <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: assignMatchCount > 0 ? "#16a34a" : "#dc2626" }}>
                    {assignMatchCount > 0 ? `${assignMatchCount} product${assignMatchCount !== 1 ? "s" : ""} will be assigned` : "No products match the current filters"}
                  </div>
                )}
              </div>

              {/* Inspection Number selector */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ ...styles.compactLabel, fontSize: 13 }}>Select Template Number <span style={{ color: "#dc2626" }}>*</span></label>
                <Select
                  options={templateOptions}
                  value={assignGtpSelected}
                  onChange={setAssignGtpSelected}
                  placeholder="Search and select a Template..."
                  isClearable styles={customSelectStyles}
                />
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Select a template number to assign to the matched products.</div>
              </div>

              {/* Characteristics with std values */}
              {assignGtpCharacteristics.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                    Standard Values <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(will apply to all matched products)</span>
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={{ ...styles.erpTh, width: "18%" }}>Process</th>
                          <th style={{ ...styles.erpTh, width: "20%" }}>Parameter</th>
                          <th style={{ ...styles.erpTh, width: "16%" }}>Variable / Formula</th>
                          <th style={{ ...styles.erpTh, width: "7%"  }}>UOM</th>
                          <th style={{ ...styles.erpTh, width: "11%" }}>Test Type</th>
                          <th style={{ ...styles.erpTh, width: "8%"  }}>Type</th>
                          <th style={{ ...styles.erpTh, width: "20%" }}>Std Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignGtpCharacteristics.map((c, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.processName || "—"}</td>
                            <td style={{ ...styles.erpTd, fontSize: 13 }}>{c.characteristic}</td>
                            <td style={{ ...styles.erpTd, fontSize: 12 }}>
                              {c.isFormula
                                ? <span title="Formula" style={{ fontFamily: "monospace", background: "#eef2ff", color: "#3730a3", padding: "2px 6px", borderRadius: 4 }}>ƒ = {c.formula || "—"}</span>
                                : (c.variable || "—")}
                            </td>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.uom || "—"}</td>
                            <td style={{ ...styles.erpTd, fontSize: 12 }}>{c.testType || "—"}</td>
                            <td style={{ ...styles.erpTd, fontSize: 12 }}>{c.type || "—"}</td>
                            <td style={{ ...styles.erpTd, padding: "6px 10px" }}>
                              <input type="text" className="form-control form-control-sm" placeholder={c.isFormula ? "Auto" : ""}
                                value={assignStdValues[c.characteristic] || ""}
                                readOnly={!!c.isFormula}
                                title={c.isFormula ? "Calculated automatically from the formula" : ""}
                                style={c.isFormula ? { background: "#f1f5f9", fontFamily: "monospace", fontWeight: 600 } : undefined}
                                onChange={e => setAssignStdValues(prev => ({ ...prev, [c.characteristic]: e.target.value }))} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div style={{ ...styles.modalFooter, gap: 10 }}>
              <button style={{ ...styles.saveBtn, background: "#64748b" }} onClick={() => { setShowAssignModal(false); setAssignGtpSelected(null); setAssignGtpCharacteristics([]); setAssignStdValues({}); }}>Cancel</button>
              <button
                style={{ ...styles.saveBtn, opacity: (!assignGtpSelected || assignSaving) ? 0.6 : 1, cursor: (!assignGtpSelected || assignSaving) ? "not-allowed" : "pointer" }}
                disabled={!assignGtpSelected || assignSaving}
                onClick={handleAssignSave}
              >
                {assignSaving ? "Saving..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════
          MODAL — VIEW / EDIT ASSIGNMENT
      ═══════════════════════════════════════════════ */}
      {showEditAssignModal && editAssignRow && (
        <div style={styles.modalBackdrop}>
          <div style={{ ...styles.modal, maxWidth: 860, height: "auto", maxHeight: "92vh" }}>
            <div style={styles.modalHeader}>
              <div>
                <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>
                  {editAssignMode === "edit" ? "Edit Assignment" : ""}
                  {" "}<span style={{ color: "#021843" }}>{editAssignRow.inspection_no}</span>
                </h5>
              </div>
              <button style={styles.closeBtn} onClick={() => { setShowEditAssignModal(false); setEditAssignRow(null); setEditAssignChars([]); setEditAssignStdValues({}); }}>✖</button>
            </div>

            <div style={{ ...styles.modalBody, padding: "24px 28px", overflowY: "auto" }}>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[
                    ["Category",    editAssignRow.product_category],
                    ["Cable Type",  editAssignRow.cable_type],
                    ["Construction",editAssignRow.construction],
                    ["Test Type",   editAssignRow.test_type],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                      <span style={{ color: "#64748b", fontWeight: 600 }}>{label}: </span>
                      <span style={{ color: "#1e293b" }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {editAssignChars.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                    Standard Values
                    {editAssignMode === "edit" && (
                      <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8", marginLeft: 8 }}>
                        (changes will apply to all linked products)
                      </span>
                    )}
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          <th style={{ ...styles.erpTh, width: "22%" }}>Process</th>
                          <th style={{ ...styles.erpTh, width: "28%" }}>Parameter</th>
                          <th style={{ ...styles.erpTh, width: "18%" }}>Variable / Formula</th>
                          <th style={{ ...styles.erpTh, width: "8%"  }}>UOM</th>
                          <th style={{ ...styles.erpTh, width: "24%" }}>Standard Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editAssignChars.map((c, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.processName || "—"}</td>
                            <td style={{ ...styles.erpTd, fontSize: 13 }}>{c.characteristic}</td>
                            <td style={{ ...styles.erpTd, fontSize: 12 }}>
                              {c.isFormula
                                ? <span title="Formula" style={{ fontFamily: "monospace", background: "#eef2ff", color: "#3730a3", padding: "2px 6px", borderRadius: 4 }}>ƒ = {c.formula || "—"}</span>
                                : (c.variable || "—")}
                            </td>
                            <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{c.uom || "—"}</td>
                            <td style={{ ...styles.erpTd, padding: "6px 10px" }}>
                              {editAssignMode === "edit" ? (
                                <input type="text" className="form-control form-control-sm" placeholder={c.isFormula ? "Auto" : ""}
                                  value={editAssignStdValues[c.characteristic] ?? ""}
                                  readOnly={!!c.isFormula}
                                  title={c.isFormula ? "Calculated automatically from the formula" : ""}
                                  style={c.isFormula ? { background: "#f1f5f9", fontFamily: "monospace", fontWeight: 600 } : undefined}
                                  onChange={e => setEditAssignStdValues(prev => ({ ...prev, [c.characteristic]: e.target.value }))} />
                              ) : (
                                <span style={{ fontSize: 13, color: "#1e293b" }}>{editAssignStdValues[c.characteristic] || "—"}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div style={{ ...styles.modalFooter, gap: 10 }}>
              {editAssignMode === "view" ? (
                <button style={styles.saveBtn} onClick={() => setEditAssignMode("edit")}>Edit</button>
              ) : (
                <>
                  <button style={{ ...styles.saveBtn, background: "#64748b" }} onClick={() => setEditAssignMode("view")}>Cancel</button>
                  <button style={{ ...styles.saveBtn, opacity: editAssignSaving ? 0.6 : 1 }} disabled={editAssignSaving} onClick={handleEditAssignSave}>
                    {editAssignSaving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InspectionAssignmentTab;